/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

/** @typedef {import('./FileHandle.js')} FileHandle */
/** @typedef {Symbol} NavClass */
/**
 * Enum for navigation classes.
 *
 * @readonly
 * @enum {NavClass}
 *
 * @property {NavClass} SIMPLE - non-navigable - ex. a single image
 * @property {NavClass} LINEAR - Linear navigation, ex. pages in a PDF or word document
 * @property {NavClass} UNKNOWN - unknown (or unsupported) file type
 */
const NavClass = Object.freeze(
	{
		SIMPLE: Symbol('simple'),
		LINEAR: Symbol('linear'),
		UNKNOWN: Symbol('unknown'),
	});

/**
 * Flow:
 *
 * 1. Navigate (ex. to a page)
 * 2. Extract (ex. a rectangle)
 * Result: a canvas with the rasterized subimage?
 */
class FileNavigatorProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		/** @type {import('fable')} */
		this.fable;

		/** @type {any} */
		this.options;

		this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));

		if (!this.options.hasOwnProperty('HeadlightAPIURL'))
		{
			this.options.HeadlightAPIURL = '/1.0/';
		}
		this.fable.serviceManager.instantiateServiceProviderIfNotExists('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });
	}

	/**
	 * Perform a JSON GET request.
	 *
	 * @param {string} uri - The URI to get.
	 * @return {Promise<any>} - The response.
	 */
	async _get(uri)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(uri, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Perform a JSON PUT request.
	 *
	 * @param {string} uri - The URI to get.
	 * @param {any} payload - The payload to send (JSON stringified).
	 * @return {Promise<any>} - The response.
	 */
	async _put(uri, payload)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.putJSON(uri, payload, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Perform a JSON POST request.
	 *
	 * @param {string} uri - The URI to get.
	 * @param {any} payload - The payload to send (JSON stringified).
	 * @return {Promise<any>} - The response.
	 */
	async _post(uri, payload)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.postJSON(uri, payload, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Perform a JSON DELETE request.
	 *
	 * @param {string} entityType - The entity type to delete.
	 * @param {number} entityID - The entity ID to delete.
	 * @return {Promise<any>} - The response.
	 */
	async _del(entityType, entityID)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.deleteEntity(entityType, entityID, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * @param {FileHandle} pFileHandle
	 * @return {NavClass}
	 */
	getFileNavClass(pFileHandle)
	{
		if (pFileHandle.type === 'application/pdf')
		{
			return NavClass.LINEAR;
		}
		return NavClass.UNKNOWN;
	}

	/**
	 * @typedef {Object} FileHandleInfo
	 * @property {Array<FileHandle>} items
	 */

	/**
	 * @typedef {Object} NavParams
	 * TODO: define what this supports...
	 */

	/**
	 * Used to explore the file. List pages, preview them, etc.
	 *
	 * @param {FileHandle} pFileHandle
	 * @param {Object} pNavParams
	 * @return {Promise<FileHandleInfo>}
	 */
	async navigateFile(pFileHandle, pNavParams)
	{
		const navClass = this.getFileNavClass(pFileHandle);
		if (navClass !== NavClass.LINEAR)
		{
			throw new Error(`Unsupported navigation class: ${navClass.toString()}`);
		}
		return await this._navigateFileLinear(pFileHandle, pNavParams);
	}

	/**
	 * Used to explore the file. List pages, preview them, etc. For linear navigation file types.
	 *
	 * @param {FileHandle} pFileHandle
	 * @param {NavParams} pNavParams
	 * @return {Promise<FileHandleInfo>}
	 */
	async _navigateFileLinear(pFileHandle, pNavParams)
	{
		if (pFileHandle.type !== 'application/pdf')
		{
			throw new Error(`Unsupported file type: ${pFileHandle.type}`);
		}
		const nav =
		{
			items:
			[
			],
		};
		return nav;
	}

	/**
	 * Extract an item from a file.
	 *
	 * @param {FileHandle} pFileHandle
	 * @param {Object} pExtractParams - Provide { canvas, scale, index }. Canvas will be resized to the desired dimensions. Scale is the zoom factor.
	 * NOTE: index in specific to linear navigation. will rev this doc eventually
	 * @return {Promise<string>} URL for the extracted item
	 */
	async extract(pFileHandle, pExtractParams)
	{
		if (pFileHandle.subType !== 'page')
		{
			throw new Error(`Unsupported file type for extract: ${pFileHandle.type}`);
		}
		const observationID = pFileHandle.observation?.IDObservation;
		if (!observationID)
		{
			throw new Error('No observation ID for source file');
		}
		const extractParamsString = Object.entries(pExtractParams).map(([ key, value ]) => `${key}:${encodeURIComponent(value)}`).join(';');
		return `Observation/${observationID}/Extract/${encodeURIComponent(extractParamsString)}`;
	}

	/**
	 * Extract items from a file. Useful for doing a page preview
	 *
	 * @param {FileHandle} pFileHandle
	 * @param {Object} pExtractParams - Provide { items, scale }. Canvas will be resized to the desired dimensions. Scale is the zoom factor.
	 * @return {Promise<Array<string>>}
	 */
	async bulkExtract(pFileHandle, pExtractParams)
	{
		const result = [];
		for (const item of pExtractParams.items)
		{
			//FIXME: probably too slow like this...
			//FIXME: this assumes each page has the same paramemeters...
			result.push(await this.extract(item, pExtractParams));
		}
		return result;
	}

	/**
	 * Extract a subimage from a file and clone it to a new image observation.
	 *
	 * @param {FileHandle} pFileHandle The source observation.
	 * @param {Object} pExtractParams The extract parameters: { Page: number, Bounds: { } } (bounds from openseadragon)
	 */
	async cloneExtract(pFileHandle, pExtractParams)
	{
		if (pFileHandle.subType !== 'page')
		{
			throw new Error(`Unsupported file type for extract: ${pFileHandle.type}`);
		}
		const observationID = pFileHandle.observation?.IDObservation;
		if (!observationID)
		{
			throw new Error('No observation ID for source file');
		}
		await this._post(`Observation/${observationID}/Extract/Clone`, pExtractParams);
	}
}

module.exports = FileNavigatorProvider;
module.exports.NavClass = NavClass;
module.exports.default_configuration = {};
